/**
 * Public product detail page.
 * Route: /catalogo/p/:id?v=VENDOR_ID
 *
 * Two-column premium layout inspired by modern e-commerce (Lucient/Aesop):
 * - Left: large product image with category pill, warm cream background.
 * - Right: brand, name, description, quantity stepper, total, CTAs (WhatsApp + Buy).
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Minus, Plus, WhatsappLogo, ShoppingCart, MagnifyingGlassPlus } from "@phosphor-icons/react";
import PhotoLightbox from "@/components/PhotoLightbox";
import { useSpecSchemas } from "@/lib/specSchemas";
import { ListChecks } from "@phosphor-icons/react";
import { useCart } from "@/context/CartContext";
import { CartFloatingButton, CartDrawer } from "@/components/storefront/CartDrawer";
import ShippingEstimator from "@/components/storefront/ShippingEstimator";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtPrice = (n, cur) =>
  `${cur === "USD" ? "US$" : "$"}${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function StorefrontProduct() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const v = params.get("v") || "";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activePhoto, setActivePhoto] = useState(0);
  const [qty, setQty] = useState(1);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const { addItem } = useCart();

  // Persist referral
  useEffect(() => {
    if (v) { try { localStorage.setItem("alendra_referrer", v); } catch { /* ignore */ } }
  }, [v]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    const url = `${API}/api/public/storefront/${id}${v ? `?v=${encodeURIComponent(v)}` : ""}`;
    axios.get(url)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id, v]);

  // Inject SEO meta tags into <head> for Google indexing. Cleans up on unmount.
  useEffect(() => {
    const product = data?.product;
    if (!product) return undefined;

    const fallbackTitle = `${product.name || "Producto"} — ${product.brand || ""} | Alendra PC`.trim();
    const fallbackDesc = (product.characteristics || product.name || "Producto disponible en Alendra PC").slice(0, 160);

    const title = (product.meta_title || fallbackTitle).trim();
    const description = (product.meta_description || fallbackDesc).trim();
    const keywords = (product.keywords || "").trim();
    const photo = (product.photos && product.photos[0]) || "";
    const canonical = `${window.location.origin}/catalogo/p/${product.id}`;

    const prevTitle = document.title;
    document.title = title;

    const upsertMeta = (selector, attrs) => {
      let tag = document.head.querySelector(selector);
      const created = !tag;
      if (!tag) {
        tag = document.createElement("meta");
        document.head.appendChild(tag);
      }
      Object.entries(attrs).forEach(([k, v]) => tag.setAttribute(k, v));
      return { tag, created };
    };
    const upsertLink = (rel, href) => {
      let tag = document.head.querySelector(`link[rel="${rel}"]`);
      const created = !tag;
      if (!tag) {
        tag = document.createElement("link");
        tag.setAttribute("rel", rel);
        document.head.appendChild(tag);
      }
      tag.setAttribute("href", href);
      return { tag, created };
    };

    const handles = [
      upsertMeta('meta[name="description"]', { name: "description", content: description }),
      upsertMeta('meta[name="keywords"]', { name: "keywords", content: keywords }),
      upsertMeta('meta[property="og:title"]', { property: "og:title", content: title }),
      upsertMeta('meta[property="og:description"]', { property: "og:description", content: description }),
      upsertMeta('meta[property="og:type"]', { property: "og:type", content: "product" }),
      upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonical }),
      upsertMeta('meta[property="og:image"]', { property: "og:image", content: photo }),
      upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" }),
      upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title }),
      upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description }),
      upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: photo }),
      upsertLink("canonical", canonical),
    ];

    return () => {
      document.title = prevTitle;
      // Only remove tags we created; leave existing global ones intact.
      handles.forEach(({ tag, created }) => { if (created && tag.parentNode) tag.parentNode.removeChild(tag); });
    };
  }, [data]);

  const backToShopUrl = useMemo(() => {
    const q = new URLSearchParams();
    if (v) q.set("v", v);
    const s = q.toString();
    return `/catalogo${s ? `?${s}` : ""}`;
  }, [v]);

  if (loading) return <DetailSkeleton />;
  if (error || !data?.product) return <NotFound backUrl={backToShopUrl} />;

  const { product, company } = data;
  const promo = product.promotion;
  const currency = promo?.currency || product.sale_currency || "MXN";
  // Use promo photos if present, fall back to product photos
  const promoPhotos = Array.isArray(promo?.photos) ? promo.photos.filter(Boolean) : [];
  const basePhotos = Array.isArray(product.photos) ? product.photos.filter(Boolean) : [];
  const photos = promoPhotos.length ? promoPhotos : basePhotos;
  const unit = promo ? Number(promo.promo_price || 0) : Number(product.store_price || 0);
  const originalUnit = Number(product.store_price || 0);
  const productForCart = promo ? {
    ...product,
    store_price: promo.promo_price,
    sale_currency: promo.currency || product.sale_currency,
  } : product;
  // Public cap: backend already capped this at min(real_stock, 5) so the
  // visitor never learns our true inventory. We just respect it in the UI.
  const maxQty = Math.max(1, Number(product.max_qty || 1));
  const safeQty = Math.min(qty, maxQty);
  const atMax = safeQty >= maxQty;
  const total = unit * safeQty;
  const totalMxn = (currency || "MXN").toUpperCase() === "USD" ? 0 : total;
  const productIds = [product.id];
  const waText = encodeURIComponent(`Hola, me interesa: ${product.name}${safeQty > 1 ? ` (cantidad ${safeQty})` : ""}${product.sku ? ` · SKU ${product.sku}` : ""}.`);
  const waUrl = company?.phone ? `https://wa.me/${String(company.phone).replace(/\D/g, "")}?text=${waText}` : null;

  return (
    <div className="min-h-screen bg-[#FAF8F2]">
      {/* Top bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between">
        <Link
          to={backToShopUrl}
          className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-zinc-600 hover:text-zinc-900 transition-colors"
          data-testid="back-to-shop"
        >
          <ArrowLeft size={14} weight="bold" /> Volver al catálogo
        </Link>
        <CartFloatingButton />
      </div>

      {/* Main layout */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 pb-16 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
        {/* Image column */}
        <section className="lg:sticky lg:top-6 lg:self-start">
          <button
            type="button"
            onClick={() => photos.length > 0 && setLightboxOpen(true)}
            disabled={photos.length === 0}
            className="group relative w-full bg-white rounded-3xl border border-zinc-100 overflow-hidden aspect-[4/5] flex items-center justify-center cursor-zoom-in disabled:cursor-default"
            data-testid="product-detail-image"
          >
            {product.category && (
              <span
                className="absolute top-5 left-5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest bg-[#D9A93B] text-white shadow-sm z-10"
                data-testid="product-detail-category"
              >
                {product.category}
              </span>
            )}
            {photos.length > 0 && (
              <span
                className="absolute top-5 right-5 w-9 h-9 rounded-full bg-white/95 backdrop-blur-sm shadow-sm flex items-center justify-center text-zinc-700 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                aria-hidden
              >
                <MagnifyingGlassPlus size={16} weight="bold" />
              </span>
            )}
            {photos[activePhoto] ? (
              <img
                src={photos[activePhoto]}
                alt={product.name}
                className="max-w-[78%] max-h-[78%] object-contain transition-transform duration-500 group-hover:scale-[1.03]"
              />
            ) : (
              <div className="text-zinc-300 text-xs font-mono uppercase tracking-wider">Sin foto</div>
            )}
          </button>
          {photos.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto">
              {photos.map((p, i) => (
                <button
                  key={i}
                  onClick={() => setActivePhoto(i)}
                  className={`shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${i === activePhoto ? "border-[#002FA7]" : "border-transparent hover:border-zinc-300"} bg-white`}
                  data-testid={`product-thumb-${i}`}
                >
                  <img src={p} alt={`${product.name} ${i + 1}`} className="w-full h-full object-contain" />
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Info column */}
        <section className="space-y-6">
          {promo && (
            <div className="inline-flex items-center gap-2" data-testid="promo-banner">
              <span className="bg-[#E0000F] text-white text-xs font-extrabold px-3 py-1 rounded tracking-wider shadow">
                {promo.badge_label || "PROMO"}
              </span>
              {originalUnit > unit && (
                <span className="text-[#E0000F] text-xs font-bold">
                  Ahorra {fmtPrice(originalUnit - unit, currency)} ({Math.round((1 - unit / originalUnit) * 100)}%)
                </span>
              )}
            </div>
          )}
          {product.brand && (
            <div className="italic text-zinc-500 text-sm" data-testid="product-detail-brand">{product.brand}</div>
          )}
          <h1 className="font-heading font-extrabold text-5xl sm:text-6xl leading-none tracking-tight text-zinc-900" data-testid="product-detail-name">
            {promo?.name || product.name}
          </h1>
          {promo && (
            <div className="text-sm text-zinc-500" data-testid="promo-product-name">{product.name}</div>
          )}

          <ConditionBanner product={product} />

          {(promo?.description || product.characteristics) && (
            <p className="text-zinc-600 text-base leading-relaxed max-w-prose whitespace-pre-line" data-testid="product-detail-description">
              {promo?.description || product.characteristics}
            </p>
          )}

          <SpecSheet product={product} />

          {/* SEO text — visible para Google y para el comprador.
              Solo se muestra si Alendra AI generó contenido SEO para este producto. */}
          {product.seo_text && (
            <div className="text-zinc-500 text-sm leading-relaxed max-w-prose pt-1 border-t border-zinc-100" data-testid="product-detail-seo-text">
              {product.seo_text}
            </div>
          )}

          {promo && originalUnit > unit && (
            <div className="inline-flex items-baseline gap-3" data-testid="promo-price-block">
              <span className="text-sm text-zinc-400 line-through numeric">{fmtPrice(originalUnit, product.sale_currency || currency)}</span>
              <span className="text-2xl font-extrabold text-[#E0000F] numeric">{fmtPrice(unit, currency)}</span>
            </div>
          )}

          {/* Quantity + Total card */}
          <div className="bg-white border border-zinc-100 rounded-2xl p-5 sm:p-6 shadow-sm" data-testid="product-detail-priceblock">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">Cantidad</span>
                <div className="inline-flex items-center bg-zinc-50 border border-zinc-200 rounded-full">
                  <button
                    type="button"
                    onClick={() => setQty((q) => Math.max(1, q - 1))}
                    disabled={safeQty <= 1}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-full hover:bg-zinc-100 disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid="qty-minus"
                    aria-label="Disminuir"
                  >
                    <Minus size={14} weight="bold" />
                  </button>
                  <span className="w-8 text-center font-mono text-sm font-semibold" data-testid="qty-value">{safeQty}</span>
                  <button
                    type="button"
                    onClick={() => setQty((q) => Math.min(maxQty, q + 1))}
                    disabled={atMax}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-full hover:bg-zinc-100 disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid="qty-plus"
                    aria-label="Aumentar"
                  >
                    <Plus size={14} weight="bold" />
                  </button>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Total</div>
                <div className="font-heading text-3xl sm:text-4xl font-extrabold text-zinc-900 leading-none" data-testid="product-detail-total">
                  {fmtPrice(total, currency)}
                </div>
                {safeQty > 1 && (
                  <div className="text-[10px] text-zinc-400 mt-1 numeric">{fmtPrice(unit, currency)} × {safeQty}</div>
                )}
              </div>
            </div>
            {atMax && (
              <div className="mt-3 pt-3 border-t border-zinc-100 text-[11px] text-zinc-500 italic" data-testid="qty-cap-hint">
                ¿Necesitas más unidades? Solicita por WhatsApp para confirmar disponibilidad.
              </div>
            )}
          </div>

          {/* CTAs */}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr] gap-3">
            <button
              type="button"
              onClick={() => addItem(productForCart, safeQty)}
              disabled={maxQty < 1}
              className="h-14 px-6 rounded-full bg-zinc-900 hover:bg-zinc-800 disabled:bg-zinc-300 disabled:cursor-not-allowed text-white font-semibold text-sm inline-flex items-center justify-center gap-2 shadow-lg shadow-zinc-900/20 transition-colors"
              data-testid="product-buy-btn"
            >
              <ShoppingCart size={16} weight="bold" /> Agregar al carrito
            </button>
            {waUrl ? (
              <a
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="h-14 px-6 rounded-full border-2 border-zinc-900 hover:bg-zinc-50 text-zinc-900 font-semibold text-sm inline-flex items-center justify-center gap-2 transition-colors"
                data-testid="product-whatsapp-btn"
              >
                <WhatsappLogo size={16} weight="fill" /> WhatsApp
              </a>
            ) : (
              <button
                disabled
                className="h-14 px-6 rounded-full bg-zinc-200 text-zinc-400 font-semibold text-sm inline-flex items-center justify-center gap-2 cursor-not-allowed"
                title="Configura el WhatsApp en Ajustes"
              >
                <WhatsappLogo size={16} weight="fill" /> WhatsApp
              </button>
            )}
          </div>

          {/* Envío según ubicación (local gratis / BC / resto de México) */}
          <ShippingEstimator productIds={productIds} subtotalMxn={totalMxn} />

          {product.condition_notes && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4 text-sm text-zinc-700 whitespace-pre-line" data-testid="product-condition-notes">
              <div className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold mb-1">Condiciones</div>
              {product.condition_notes}
            </div>
          )}

          {/* Meta footer */}
          <div className="pt-4 border-t border-zinc-200 text-[11px] text-zinc-400 font-mono uppercase tracking-widest">
            {product.sku && <span>SKU {product.sku}</span>}
            {product.sku && product.category && <span> · </span>}
            {product.category && <span>{product.category}</span>}
          </div>

          <AestheticDetails product={product} />
        </section>
      </main>

      <PhotoLightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        photos={photos}
        initialIndex={activePhoto}
        title={product.name}
        filenameBase={product.name}
      />

      <CartDrawer />
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="min-h-screen bg-[#FAF8F2]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5">
        <div className="h-3 w-32 bg-zinc-200 animate-pulse rounded" />
      </div>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 pb-16 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
        <div className="aspect-[4/5] bg-zinc-100 rounded-3xl animate-pulse" />
        <div className="space-y-4">
          <div className="h-4 w-20 bg-zinc-200 animate-pulse rounded" />
          <div className="h-12 w-3/4 bg-zinc-200 animate-pulse rounded" />
          <div className="h-3 w-full bg-zinc-100 animate-pulse rounded" />
          <div className="h-3 w-5/6 bg-zinc-100 animate-pulse rounded" />
          <div className="h-24 bg-zinc-100 animate-pulse rounded-2xl mt-6" />
          <div className="h-14 bg-zinc-100 animate-pulse rounded-full" />
        </div>
      </main>
    </div>
  );
}

function NotFound({ backUrl }) {
  return (
    <div className="min-h-screen bg-[#FAF8F2] flex items-center justify-center px-4">
      <div className="text-center">
        <h1 className="font-heading text-4xl font-extrabold text-zinc-900 mb-2">Producto no disponible</h1>
        <p className="text-zinc-500 mb-6">Es posible que se haya agotado o que el enlace no sea válido.</p>
        <Link
          to={backUrl}
          className="inline-flex items-center gap-2 px-5 py-3 bg-zinc-900 hover:bg-zinc-800 text-white rounded-full font-semibold text-sm"
          data-testid="notfound-back"
        >
          <ArrowLeft size={14} weight="bold" /> Volver al catálogo
        </Link>
      </div>
    </div>
  );
}


/**
 * Banner explicativo de la condición del equipo, visible al cliente
 * justo debajo del título. Color codificado:
 *   - Nuevo: verde
 *   - Semi-nuevo: ámbar
 *   - Reacondicionado: violeta
 * Si el producto tiene un grado estético (A/B/C), también lo muestra
 * con un chip secundario. Para grado B/C agrega un "ver detalles" que
 * lleva al cliente a la sección de fotos marcadas.
 */
function ConditionBanner({ product }) {
  const cond = product?.condition || "nuevo";
  const grade = product?.aesthetic_grade || null;
  const map = {
    nuevo:           { label: "Nuevo",           border: "border-emerald-200", bg: "bg-emerald-50",  dot: "bg-emerald-500", title: "text-emerald-800", desc: "Equipo nuevo en empaque original." },
    seminuevo:       { label: "Semi-nuevo",      border: "border-amber-200",   bg: "bg-amber-50",    dot: "bg-amber-500",   title: "text-amber-800",   desc: "Equipo poco usado, en excelente estado general." },
    reacondicionado: { label: "Reacondicionado", border: "border-violet-200",  bg: "bg-violet-50",   dot: "bg-violet-600",  title: "text-violet-800",  desc: "Equipo revisado, probado y restaurado por nuestro equipo técnico." },
  };
  const m = map[cond] || map.nuevo;
  const gradeMap = {
    A: { label: "Grado A · sin detalles",  bg: "bg-emerald-100",  text: "text-emerald-800" },
    B: { label: "Grado B · detalles leves",bg: "bg-amber-100",    text: "text-amber-800" },
    C: { label: "Grado C · detalles más visibles", bg: "bg-red-100", text: "text-red-800" },
  };
  const g = grade ? gradeMap[grade] : null;
  return (
    <div className={`inline-flex flex-wrap items-center gap-3 px-3 py-2 rounded-md ${m.bg} ${m.border} border`} data-testid="condition-banner">
      <span className={`w-2 h-2 rounded-full ${m.dot}`} aria-hidden />
      <span className={`text-xs font-bold uppercase tracking-wider ${m.title}`}>{m.label}</span>
      <span className="text-xs text-zinc-600">— {m.desc}</span>
      {g && (
        <a
          href="#detalles-esteticos"
          className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${g.bg} ${g.text} hover:opacity-80 transition-opacity`}
          data-testid="condition-banner-grade"
        >
          {g.label}
        </a>
      )}
    </div>
  );
}

/**
 * Galería de fotos donde se marcan los detalles estéticos del equipo.
 * Solo se renderiza cuando el producto tiene grado B o C con fotos cargadas.
 * El cliente puede hacer click para verlas en grande (reutiliza el
 * PhotoLightbox del producto principal vía el evento de Lightbox global).
 */
function AestheticDetails({ product }) {
  const grade = product?.aesthetic_grade || null;
  const photos = Array.isArray(product?.aesthetic_photos) ? product.aesthetic_photos.filter(Boolean) : [];
  const notes = (product?.aesthetic_notes || "").trim();
  const [lbOpen, setLbOpen] = useState(false);
  const [lbIdx, setLbIdx] = useState(0);
  if (!grade || (grade !== "B" && grade !== "C") || photos.length === 0) return null;
  const tone = grade === "C" ? "red" : "amber";
  const palette = {
    amber: { ring: "border-amber-200", bg: "bg-amber-50/70", chip: "bg-amber-600", title: "text-amber-900" },
    red:   { ring: "border-red-200",   bg: "bg-red-50/70",   chip: "bg-red-600",   title: "text-red-900" },
  }[tone];
  return (
    <section id="detalles-esteticos" className={`mt-6 p-4 rounded-xl border ${palette.ring} ${palette.bg}`} data-testid="aesthetic-section">
      <div className="flex items-center gap-2 mb-2">
        <span className={`${palette.chip} text-white text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider`}>Grado {grade}</span>
        <h3 className={`font-heading text-lg font-extrabold ${palette.title}`}>Detalles estéticos</h3>
      </div>
      <p className="text-sm text-zinc-700 mb-3">
        Este equipo tiene detalles de uso visibles. A continuación mostramos exactamente dónde, para que tu compra sea 100% informada.
      </p>
      {notes && (
        <p className="text-sm text-zinc-600 mb-3 whitespace-pre-line italic" data-testid="aesthetic-notes">"{notes}"</p>
      )}
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="aesthetic-photos-public">
        {photos.map((p, i) => (
          <button
            key={p + i}
            type="button"
            onClick={() => { setLbIdx(i); setLbOpen(true); }}
            className="relative aspect-square rounded-md overflow-hidden border border-white bg-zinc-100 hover:ring-2 hover:ring-offset-1 hover:ring-zinc-400 transition-shadow cursor-zoom-in"
            data-testid={`aesthetic-photo-${i}`}
          >
            <img src={p} alt={`Detalle ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
            <span className="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] font-mono px-1.5 py-0.5 rounded">#{i + 1}</span>
          </button>
        ))}
      </div>
      <PhotoLightbox
        open={lbOpen}
        onClose={() => setLbOpen(false)}
        photos={photos}
        initialIndex={lbIdx}
        title={`${product.name} — Detalles estéticos`}
        filenameBase={`${product.name}-detalles`}
      />
    </section>
  );
}

// Ficha técnica: campos del esquema de la categoría en orden + specs extra.
function SpecSheet({ product }) {
  const schemas = useSpecSchemas();
  const rows = schemas.ordered(product.category, product.specs);
  if (rows.length === 0) return null;
  return (
    <section className="bg-white border border-zinc-100 rounded-2xl p-5 sm:p-6 shadow-sm" data-testid="product-spec-sheet">
      <h2 className="font-heading text-base md:text-lg font-bold text-zinc-900 flex items-center gap-2">
        <ListChecks size={18} weight="duotone" className="text-[#002FA7]" /> Ficha técnica
      </h2>
      <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6">
        {rows.map((r) => (
          <div key={r.key} className="flex justify-between gap-3 py-2 border-b border-zinc-100 text-sm" data-testid={`spec-row-${r.key}`}>
            <dt className="text-zinc-500">{r.label}</dt>
            <dd className="text-zinc-900 font-medium text-right">{r.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
